import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Fade,
} from '@mui/material';
import {
  Download,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { pwaService } from '../services/pwaService';
import { pwaAuthService } from '../services/pwaAuthService';

export const PWAInstallPage: React.FC = () => {
  const navigate = useNavigate();
  const [installStatus, setInstallStatus] = useState<'not-installed' | 'installed' | 'not-supported'>('not-installed');
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    const status = pwaService.getInstallationStatus();
    setInstallStatus(status);
    
    const available = await pwaAuthService.isBiometricAvailable();
    setBiometricAvailable(available);
    
    pwaService.resetPromptDismissal();
  };

  const handleInstall = async () => {
    setLoading(true);
    try {
      const success = await pwaService.showInstallPrompt();
      if (success) {
        setInstallStatus('installed');
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
    } finally {
      setLoading(false);
    }
  };



  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };



  const installationSteps = {
    0: [
      'Click "Install" or "Add to Home Screen" in the address bar',
      'Confirm the installation',
      'Find the app on your desktop or start menu',
    ],
    1: [
      'Open Chrome browser on your Android device',
      'Tap the menu (three dots) in the top right',
      'Select "Add to Home screen"',
      'Tap "Add" to confirm',
    ],
    2: [
      'Open Safari browser on your iPhone or iPad',
      'Tap the Share button (square with arrow)',
      'Scroll down and tap "Add to Home Screen"',
      'Tap "Add" to confirm',
    ],
  };

  const tabLabels = ['Windows/Mac (Chrome/Edge)', 'Android', 'iOS (Safari)'];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box display="flex" flexDirection="column" alignItems="center" mb={8}>
          <Box display="flex" alignItems="center" mb={4}>
            <Box
              component="img"
              src="https://www.ngworkforce.com/wp-content/uploads/2024/06/NextGenWorkforce-4-1-2048x567.png"
              alt="NextGen Workforce"
              sx={{
                height: 60,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
          <Typography variant="h2" fontWeight={700} textAlign="center" color="#1F2937" mb={2}>
            Install NextGen Workforce
          </Typography>
          <Typography variant="h5" textAlign="center" color="#6B7280" fontWeight={400}>
            Get the app on your device for a better experience
          </Typography>
        </Box>



        {/* How to Install Section */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #E5E7EB',
            borderRadius: 3,
            p: 4,
            mb: 6,
          }}
        >
          <Typography variant="h5" fontWeight={600} color="#1F2937" mb={4}>
            How to install
          </Typography>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: '#E5E7EB', mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: '#6B7280',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                },
                '& .Mui-selected': { color: '#3B82F6' },
                '& .MuiTabs-indicator': { backgroundColor: '#3B82F6' },
              }}
            >
              {tabLabels.map((label, index) => (
                <Tab key={index} label={label} />
              ))}
            </Tabs>
          </Box>

          {/* Installation Steps */}
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box>
                {installationSteps[activeTab as keyof typeof installationSteps].map((step, index) => (
                  <Box key={index} display="flex" alignItems="flex-start" mb={3}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body1" color="#374151">
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Browser Mockup */}
                <Box
                  sx={{
                    height: 120,
                    backgroundColor: '#F3F4F6',
                    border: '1px solid #D1D5DB',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 24,
                      height: 24,
                      backgroundColor: '#3B82F6',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Download sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                  <Typography variant="body2" color="#6B7280">
                    Browser Address Bar
                  </Typography>
                </Box>
                
                {/* Dialog Mockup */}
                <Box
                  sx={{
                    height: 80,
                    backgroundColor: '#F3F4F6',
                    border: '1px solid #D1D5DB',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" color="#6B7280" fontWeight={500}>
                    Install Confirmation
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Install Button */}
        <Box textAlign="center" mb={6}>
          {installStatus === 'installed' ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <CheckCircle sx={{ color: '#10B981', mr: 1 }} />
              <Typography variant="h6" color="#10B981" fontWeight={500}>
                App is installed!
              </Typography>
            </Box>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={<Download />}
              onClick={handleInstall}
              disabled={loading || installStatus === 'not-supported'}
              sx={{
                backgroundColor: '#3B82F6',
                color: 'white',
                px: 6,
                py: 2,
                fontSize: '1.125rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#2563EB' },
                '&:disabled': { backgroundColor: '#9CA3AF' },
              }}
            >
              {loading ? 'Installing...' : 'Install Now'}
            </Button>
          )}
          
          <Typography variant="body2" color="#6B7280" mt={2}>
            Not seeing the installation option?{' '}
            <Typography
              component="a"
              href="https://support.ngworkforce.com/open.php"
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              color="#3B82F6"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              Open Support Ticket
            </Typography>
          </Typography>
        </Box>

        {/* Footer */}
        <Box textAlign="center">
          <Typography variant="h6" color="#1F2937" fontWeight={600} mb={2}>
            Important Considerations
          </Typography>
          <Typography variant="body2" color="#6B7280">
            Only install on your own device—not on public computers.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}; 